import { create } from 'zustand';
import { api, fromUnix, toQueryString } from '@/lib/api';
import type { ApiResponse, Contact } from '@nexus/shared/types';
import { toast } from './toastStore';

interface ContactsState {
  items: Contact[];
  selected: Contact | null;
  total: number;
  page: number;
  isLoading: boolean;
  error: string | null;
  fetch: (params?: Record<string, string | number | undefined>) => Promise<void>;
  fetchOne: (id: string) => Promise<void>;
  create: (data: Record<string, unknown>) => Promise<Contact>;
  update: (id: string, data: Record<string, unknown>) => Promise<void>;
  remove: (id: string) => Promise<void>;
  bulkUpdate: (ids: string[], updates: Record<string, unknown>) => Promise<void>;
  bulkDelete: (ids: string[]) => Promise<void>;
  clearSelected: () => void;
  clearError: () => void;
}

function convertDates(c: Record<string, unknown>): Contact {
  return {
    ...c,
    createdAt: fromUnix(c.createdAt as number),
    updatedAt: fromUnix(c.updatedAt as number),
    lastInteractionAt: c.lastInteractionAt ? fromUnix(c.lastInteractionAt as number) : undefined,
  } as Contact;
}

export const useContactsStore = create<ContactsState>((set, get) => ({
  items: [],
  selected: null,
  total: 0,
  page: 1,
  isLoading: false,
  error: null,

  fetch: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get<ApiResponse<Contact[]>>(`/api/contacts${toQueryString(params)}`);
      set({
        items: (res.data ?? []).map(c => convertDates(c as unknown as Record<string, unknown>)),
        total: res.meta?.total ?? 0,
        page: res.meta?.page ?? 1,
        isLoading: false,
      });
    } catch (err: unknown) {
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  fetchOne: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get<ApiResponse<Contact>>(`/api/contacts/${id}`);
      if (res.data) set({ selected: convertDates(res.data as unknown as Record<string, unknown>), isLoading: false });
    } catch (err: unknown) {
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  create: async (data) => {
    try {
      const res = await api.post<ApiResponse<Contact>>('/api/contacts', data);
      const contact = convertDates(res.data as unknown as Record<string, unknown>);
      set((s) => ({ items: [contact, ...s.items], total: s.total + 1 }));
      toast.success('Contact created', `${(data as Record<string, string>).name || 'New contact'} has been added`);
      return contact;
    } catch (err) {
      toast.error('Failed to create contact', (err as Error).message);
      throw err;
    }
  },

  update: async (id, data) => {
    try {
      const res = await api.patch<ApiResponse<Contact>>(`/api/contacts/${id}`, data);
      const contact = convertDates(res.data as unknown as Record<string, unknown>);
      set((s) => ({
        items: s.items.map((c) => (c.id === id ? contact : c)),
        selected: s.selected?.id === id ? contact : s.selected,
      }));
      toast.success('Contact updated');
    } catch (err) {
      toast.error('Failed to update contact', (err as Error).message);
      throw err;
    }
  },

  remove: async (id) => {
    try {
      await api.del(`/api/contacts/${id}`);
      set((s) => ({
        items: s.items.filter((c) => c.id !== id),
        total: s.total - 1,
        selected: s.selected?.id === id ? null : s.selected,
      }));
      toast.success('Contact deleted');
    } catch (err) {
      toast.error('Failed to delete contact', (err as Error).message);
      throw err;
    }
  },

  bulkUpdate: async (ids, updates) => {
    try {
      await api.patch('/api/contacts/bulk', { ids, updates });
      toast.success(`Updated ${ids.length} contact${ids.length !== 1 ? 's' : ''}`);
      get().fetch();
    } catch (err) {
      toast.error('Bulk update failed', (err as Error).message);
      throw err;
    }
  },

  bulkDelete: async (ids) => {
    try {
      await api.del('/api/contacts/bulk', { ids });
      toast.success(`Deleted ${ids.length} contact${ids.length !== 1 ? 's' : ''}`);
      get().fetch();
    } catch (err) {
      toast.error('Bulk delete failed', (err as Error).message);
      throw err;
    }
  },

  clearSelected: () => set({ selected: null }),
  clearError: () => set({ error: null }),
}));
